'use strict'

const IDLE_WORKERS = 2
const BOOTSTRAPPED_WORKERS = 2
const wixCodeMessageTypes = require('../wixCodeMessageTypes')
const {shouldSendMessageToWorker} = require('./messages')

const bootstrapIdentity = msg => {
    const applications = JSON.parse(msg.data.bootstrapArguments.applications)

    return applications.map(app => app.id).sort().join(',')
}

class WorkersManager {
    constructor(queryParams) {
        this.idleWorkers = []
        this.standbyBootstrappedWorkers = new Map()
        this.activeWorkersByContextId = new Map()
        this.queryParams = queryParams
    }
    setMessageFromWorkerHandler(handler) {
        this.messageFromWorkerHandler = handler
    }
    init(contextId) {
        const activeWorker = this.activeWorkersByContextId.get(contextId)
        if (!activeWorker) {
            this._createActiveWorker(contextId)
            this._ensureIdleWorkers()
        }
    }
    _createActiveWorker(contextId) {
        const activeWorker = this._createWorker(this.queryParams)
        this.activeWorkersByContextId.set(contextId, activeWorker)
        activeWorker.setContextId(contextId)
        activeWorker.setActive(true)
    }
    terminate(contextId) {
        const activeWorker = this.activeWorkersByContextId.get(contextId)
        if (activeWorker) {
            activeWorker.terminate()
            this.activeWorkersByContextId.delete(contextId)
        }
    }
    messageToWorker(contextId, message, transfer) {
        if (!shouldSendMessageToWorker(message.data)) {
            return
        }

        const activeWorker = this.activeWorkersByContextId.get(contextId)

        if (message.data.type !== wixCodeMessageTypes.BOOTSTRAP) {
            this._postMessage(activeWorker, message, transfer)
        } else {
            this._handleBootstrapMessage(contextId, message, transfer)
        }
    }
    _ensureIdleWorkers() {
        for (let i = IDLE_WORKERS - this.idleWorkers.length; i > 0; i--) {
            this.idleWorkers.push(this._createWorker(this.queryParams))
        }
    }
    _ensureBootstrappedWorkers(bootstrapMessage, transfer) {
        for (let i = BOOTSTRAPPED_WORKERS - this.standbyBootstrappedWorkers.get(bootstrapIdentity(bootstrapMessage)).length; i > 0; i--) {
            const aboutToBeBootstrapped = this.idleWorkers.shift()
            this._bootstrapWorker(aboutToBeBootstrapped, bootstrapMessage, transfer)
            this.standbyBootstrappedWorkers.get(bootstrapIdentity(bootstrapMessage)).push(aboutToBeBootstrapped)
        }

        this._ensureIdleWorkers()
    }
    _handleBootstrapMessage(contextId, bootstrapMessage, transfer) {
        if (!this.standbyBootstrappedWorkers.has(bootstrapIdentity(bootstrapMessage))) {
            this._terminateIrrelevantBootstrappedWorkers(bootstrapIdentity(bootstrapMessage))
        }

        const alreadyBootstrapped = this.standbyBootstrappedWorkers.get(bootstrapIdentity(bootstrapMessage)).shift()
        const activeWorker = this.activeWorkersByContextId.get(contextId)
        if (!activeWorker.bootstrapped) {
            if (alreadyBootstrapped) {
                this._swapJustCreatedWithBootstrapped(contextId, bootstrapMessage, transfer, alreadyBootstrapped)
            } else {
                this._bootstrapWorker(activeWorker, bootstrapMessage, transfer)
            }
        }

        this._ensureBootstrappedWorkers(bootstrapMessage, transfer)
    }
    _postMessage(worker, message, transfer) {
        worker.postMessage(message.data, transfer)
        if (this.queryParams.isDebug) {
            const clone = JSON.parse(JSON.stringify(message.data))
            window.iframeWorkerMessages.push({direction: 'OUTGOING', clone, arg1: transfer, timestamp: performance.now()})
        }
    }
    _createWorker(queryParmas) {
        const worker = new Worker(queryParmas.workerUrl)

        worker.active = false
        worker.bootstrapped = false
        worker.messages = []
        worker.setContextId = cId => {
            worker.contextId = cId
        }
        worker.setActive = active => {
            worker.active = active

            if (active) {
                worker.messages.forEach(messageFromWorker => {
                    this._propagateMessage(worker, messageFromWorker)
                })

                worker.messages = []
            }
        }

        worker.addEventListener('message', messageFromWorker => {
            if (!worker.active) {
                worker.messages.push(messageFromWorker)
            } else {
                this._propagateMessage(worker, messageFromWorker)
            }
        })

        return worker
    }
    _propagateMessage(worker, messageFromWorker) {
        messageFromWorker.data.__messageContextId = worker.contextId
        this.messageFromWorkerHandler(messageFromWorker)
    }
    _swapJustCreatedWithBootstrapped(contextId, bootstrapMessage, transfer, alreadyBootstrapped) {
        const stillNotBootstrapped = this.activeWorkersByContextId.get(contextId)
        stillNotBootstrapped.setActive(false)
        stillNotBootstrapped.setContextId(null)
        this.standbyBootstrappedWorkers.get(bootstrapIdentity(bootstrapMessage)).push(stillNotBootstrapped)
        this._bootstrapWorker(stillNotBootstrapped, bootstrapMessage, transfer)

        this.activeWorkersByContextId.set(contextId, alreadyBootstrapped)
        this.activeWorkersByContextId.get(contextId).setContextId(contextId)
        this.activeWorkersByContextId.get(contextId).setActive(true)
    }
    _bootstrapWorker(worker, bootstrapMessage, transfer) {
        this._postMessage(worker, bootstrapMessage, transfer)
        worker.bootstrapped = true
    }
    _terminateIrrelevantBootstrappedWorkers(relevantKey) {
        for (const workers of this.standbyBootstrappedWorkers.values()) {
            workers.forEach(worker => worker.terminate())
        }
        this.standbyBootstrappedWorkers.clear()
        this.standbyBootstrappedWorkers.set(relevantKey, [])
    }
}

module.exports = {
    WorkersManager
}
